import { useApolloClient } from '@apollo/client';
import { useState } from 'react';

import { Share07Icon } from '@/components/icons/Share07Icon';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useReportError } from '@/hooks/useReportError';
import {
  Operations,
  usePublishTenantSlideConfigMutation,
  useUnpublishTenantSlideConfigMutation,
} from '@/modules/education/EducationSlideTrowser/graphql/EducationSlideTrowser.generated';

interface EducationPagePublishingMenuItemProps {
  action: 'publish' | 'unpublish';
  configIds: string[];
}

export function EducationPagePublishingMenuItem({
  action,
  configIds,
}: EducationPagePublishingMenuItemProps) {
  const client = useApolloClient();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [publish, { loading: publishLoading }] =
    usePublishTenantSlideConfigMutation();
  const [unpublish, { loading: unpublishLoading }] =
    useUnpublishTenantSlideConfigMutation();
  const loading = isSubmitting || publishLoading || unpublishLoading;
  const actionLabel = action === 'unpublish' ? 'Unpublish' : 'Publish';

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const mutation = action === 'unpublish' ? unpublish : publish;
      const results = await Promise.allSettled(
        configIds.map((id) => mutation({ variables: { id } }))
      );
      await client.refetchQueries({
        include: [Operations.Query.GetEducationPageEditor],
      });
      const failedResult = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected'
      );
      if (failedResult) throw failedResult.reason;

      showFeedback(
        action === 'unpublish'
          ? 'Education page unpublished successfully'
          : 'Education page published successfully',
        { variant: 'success' }
      );
      setIsConfirmationOpen(false);
    } catch (error) {
      const publishError =
        error instanceof Error ? error : new Error(String(error));
      showFeedback(
        `We weren't able to ${action} this Education page. Please try again.`,
        { variant: 'error' }
      );
      reportError(`Failed to ${action} Education page`, publishError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isConfirmationOpen && (
        <ConfirmationModal
          isOpen
          heading={`${actionLabel} Education page?`}
          confirmText={actionLabel}
          closeOnOutsideClick={!loading}
          confirmButtonProps={{ loading, disabled: loading }}
          cancelButtonProps={{ disabled: loading }}
          onClose={(reason) => {
            if (reason === 'confirm') {
              void handleConfirm();
              return;
            }
            setIsConfirmationOpen(false);
          }}
        >
          {action === 'unpublish'
            ? 'Member tenants will stop receiving these overrides. Your private overrides will be preserved.'
            : 'All member tenants will receive these overrides. Future edits will be live immediately.'}
        </ConfirmationModal>
      )}
      <MenuItem
        icon={<Share07Icon size={18} />}
        label={actionLabel}
        loading={loading}
        onClick={(event) => {
          event.stopPropagation();
          setIsConfirmationOpen(true);
        }}
      />
    </>
  );
}
